// Global Instructions Rule Applied!
//
// AggregationsButton — toolbar button + drawer for the Fund Investment Detail
// grid's aggregation builder. Sits next to ViewConfig (Filtering / Sorting /
// Edit columns) and uses the same drawer + apply/reset chrome.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Drawer, Divider, Button as AntButton, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRotateLeft,
  faCheck,
  faLayerGroup,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { portalGradients } from '../../../../portal-configs/colors.js'
import Button from '../../../../core/components/Button.jsx'
import {
  AggregationPanel,
  aggregationToDraftRows,
  draftRowsToAggregation,
  type AggregationDraftRow
} from './AggregationPanel.tsx'
import { aggregationDimensionLabel, type AggregationDimensionOption } from './aggregationDimensions.ts'

export interface AggregationsButtonProps {
  /** Currently applied aggregation list. Order is significant. */
  currentAggregation: string[]
  /** Default aggregation. Used to determine "Reset to default" enablement. */
  defaultAggregation: string[]
  onApplyAggregation: (aggregation: string[]) => Promise<void> | void
  onResetToDefault: () => Promise<void> | void
  /** Optional dimension catalog. Defaults (when omitted) to the per-fund
   *  Fund Investment Detail set — the standalone Investments screen passes
   *  an extended list that includes a "Fund" dimension. */
  options?: AggregationDimensionOption[]
}

function aggArraysEqual(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
  const aa = Array.isArray(a) ? a : []
  const bb = Array.isArray(b) ? b : []
  if (aa.length !== bb.length) return false
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
  return true
}

export function AggregationsButton({
  currentAggregation,
  defaultAggregation,
  onApplyAggregation,
  onResetToDefault,
  options
}: AggregationsButtonProps) {
  const labelFor = useCallback(
    (value: string): string =>
      options?.find((o) => o.value === value)?.label ?? aggregationDimensionLabel(value),
    [options]
  )
  const [open, setOpen] = useState(false)
  const [draftRows, setDraftRows] = useState<AggregationDraftRow[]>(() =>
    aggregationToDraftRows(currentAggregation)
  )

  // Re-sync when current changes (e.g. external reset).
  useEffect(() => {
    if (!open) {
      setDraftRows(aggregationToDraftRows(currentAggregation))
    }
  }, [currentAggregation, open])

  const draftAggregation = useMemo(() => draftRowsToAggregation(draftRows), [draftRows])
  const isDirty = !aggArraysEqual(draftAggregation, currentAggregation)
  const isAlreadyDefault = aggArraysEqual(currentAggregation, defaultAggregation)
  const badgeCount = (currentAggregation || []).length

  const handleApply = useCallback(async () => {
    if (!isDirty) return
    await Promise.resolve(onApplyAggregation(draftAggregation))
    setOpen(false)
  }, [isDirty, onApplyAggregation, draftAggregation])

  const handleCancel = useCallback(() => {
    setDraftRows(aggregationToDraftRows(currentAggregation))
  }, [currentAggregation])

  const handleReset = useCallback(async () => {
    await Promise.resolve(onResetToDefault())
    setOpen(false)
  }, [onResetToDefault])

  const toolbarBtnClass =
    '!border-0 !shadow-none !bg-transparent !text-white hover:!bg-white/10 hover:!text-white [&_.ant-btn-icon]:!text-white'

  const headerStyle = useMemo(
    () => ({
      background: portalGradients.navbar,
      borderBottom: 'none',
      color: 'white'
    }),
    []
  )

  const summary = (currentAggregation || []).length === 0
    ? 'No aggregation'
    : currentAggregation.map(labelFor).join(' → ')

  return (
    <>
      <Badge count={badgeCount} size='small' offset={[0, 2]} showZero={false}>
        <Button
          variant='default'
          type='text'
          size='small'
          icon={<FontAwesomeIcon icon={faLayerGroup} />}
          className={toolbarBtnClass}
          onClick={() => setOpen(true)}
        >
          Aggregations
        </Button>
      </Badge>

      <Drawer
        title={
          <div className='flex w-full min-w-0 items-center justify-between gap-2'>
            <span className='min-w-0 flex-1 truncate text-base font-semibold leading-snug text-white'>
              Aggregations
            </span>
            <Tooltip title='Close'>
              <AntButton
                type='default'
                size='small'
                icon={<FontAwesomeIcon icon={faTimes} />}
                onClick={() => setOpen(false)}
                aria-label='Close'
                className='flex-shrink-0 bg-white/20 !border-white/40 !text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
                style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
              />
            </Tooltip>
          </div>
        }
        placement='right'
        width={400}
        open={open}
        onClose={() => setOpen(false)}
        styles={{ header: headerStyle, body: { display: 'flex', flexDirection: 'column' } }}
      >
        <div className='flex min-h-0 flex-1 flex-col gap-0'>
          <div className='shrink-0 px-1 pb-2 text-xs text-gray-500'>{summary}</div>
          <div className='flex shrink-0 flex-wrap items-center justify-end gap-1.5'>
            {isDirty ? (
              <>
                <Button variant='cancel' size='small' onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant='save'
                  size='small'
                  icon={<FontAwesomeIcon icon={faCheck} className='text-xs' />}
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </>
            ) : (
              <Button
                type='text'
                variant='default'
                size='small'
                disabled={isAlreadyDefault}
                className={!isAlreadyDefault ? '' : '!text-gray-400'}
                icon={<FontAwesomeIcon icon={faArrowRotateLeft} className='text-xs' />}
                onClick={handleReset}
              >
                Reset to default
              </Button>
            )}
          </div>
          <Divider className='!mt-1 !mb-2 shrink-0' />
          <AggregationPanel value={draftRows} onChange={setDraftRows} options={options} />
        </div>
      </Drawer>
    </>
  )
}
